import { type CapabilityAccessView, useOrgAuthStore } from "@solomon-apps/shared";
import { useQuery } from "@tanstack/react-query";

import { fetchClientCapabilityAccessResource } from "@/services/clientCapabilityAccess";

export interface UseClientCapabilityAccessReturn {
    access: CapabilityAccessView | null;
    etag: string | null;
    relationshipUuid: string | null;
    isLoading: boolean;
    error: Error | null;
    hasSelectedContext: boolean;
}

export function useClientCapabilityAccess(): UseClientCapabilityAccessReturn {
    const tokens = useOrgAuthStore((state) => state.tokens);
    const selectedClientContextUuid = useOrgAuthStore((state) => state.selectedClientContextUuid);
    const authReady = Boolean(tokens && tokens.expiresAt >= Date.now() + 30_000);
    const hasSelectedContext = Boolean(selectedClientContextUuid);
    const { data, isLoading, error } = useQuery<{ access: CapabilityAccessView; etag: string }, Error>({
        queryKey: ["yaas-client", "capability-access", selectedClientContextUuid],
        queryFn: ({ queryKey }) => {
            const relationshipUuid = queryKey[2];
            if (typeof relationshipUuid !== "string") {
                throw new Error("Select a distributor relationship before capability access is loaded");
            }
            return fetchClientCapabilityAccessResource(relationshipUuid);
        },
        enabled: authReady && hasSelectedContext,
        staleTime: 0,
        refetchOnWindowFocus: true
    });

    return {
        access: data?.access ?? null,
        etag: data?.etag ?? null,
        relationshipUuid: selectedClientContextUuid,
        isLoading: authReady && hasSelectedContext ? isLoading : false,
        error,
        hasSelectedContext
    };
}
